import { inject, Injectable } from '@angular/core';
import { LoggingService, StorageService } from '@pxl/models/contracts';

@Injectable({ providedIn: 'root' })
export class CacheService {
    private readonly loggingService = inject(LoggingService);
    private readonly storageService = inject(StorageService);

    public async clearCache(): Promise<void> {
        if ('caches' in window) {
            try {
                const cacheNames = await caches.keys();
                await Promise.all(
                    cacheNames.map((cacheName) =>
                        caches.delete(cacheName).catch((err) => {
                            this.loggingService.error(`Error deleting cache ${cacheName}:`, err);
                            return false;
                        }),
                    ),
                );
                this.loggingService.info('All caches cleared:', cacheNames);
            } catch (err) {
                this.loggingService.error('Error clearing caches:', err);
            }
        }
    }

    public async clearAll(): Promise<void> {
        sessionStorage.clear();
        await Promise.all([this.clearCache(), this.storageService.clear()]);
    }

    public async cacheSize(): Promise<number> {
        let totalSize = 0;

        if ('caches' in window) {
            try {
                const cacheNames = await caches.keys();

                for (const cacheName of cacheNames) {
                    const cache = await caches.open(cacheName);
                    const requests = await cache.keys();

                    for (const request of requests) {
                        const response = await cache.match(request);
                        if (response) {
                            const blob = await response.blob();
                            totalSize += blob.size;
                        }
                    }
                }

                this.loggingService.info(`Total cache size: ${totalSize} bytes`);
            } catch (err) {
                this.loggingService.error('Error calculating cache size:', err);
            }
        }

        return totalSize;
    }

    public async cacheObjectsCount(): Promise<number> {
        let totalCount = 0;

        if ('caches' in window) {
            try {
                const cacheNames = await caches.keys();

                for (const cacheName of cacheNames) {
                    const cache = await caches.open(cacheName);
                    const requests = await cache.keys();
                    totalCount += requests.length;
                }

                this.loggingService.info(`Total cache objects count: ${totalCount}`);
            } catch (err) {
                this.loggingService.error('Error calculating cache objects count:', err);
            }
        }

        return totalCount;
    }

    public sessionStorageCount(): number {
        let count = 0;
        try {
            count = sessionStorage.length;
            this.loggingService.info(`Session storage objects count: ${count}`);
        } catch (err) {
            this.loggingService.error('Error accessing session storage:', err);
        }
        return count;
    }

    public localStorageCount(): number {
        let count = 0;
        try {
            count = localStorage.length;
            this.loggingService.info(`Local storage objects count: ${count}`);
        } catch (err) {
            this.loggingService.error('Error accessing local storage:', err);
        }
        return count;
    }

    public async indexedDBCount(): Promise<number> {
        // storage service is using indexedDB under the hood
        return this.storageService.getStorage().length();
    }

    public allCacheObjectsCount(): Promise<number> {
        return Promise.all([
            this.cacheObjectsCount(),
            Promise.resolve(this.sessionStorageCount()),
            Promise.resolve(this.localStorageCount()),
            this.indexedDBCount(),
        ]).then((counts) => counts.reduce((acc, count) => acc + count, 0));
    }
}
